import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, Link, useLocation } from "react-router-dom";
import ScrollToTop from "@/components/ScrollToTop";
import CookieConsent from "@/components/CookieConsent";
import { useGoogleAnalytics } from "@/hooks/useGoogleAnalytics";
import { LanguageProvider } from "@/contexts/LanguageContext";
import ArabicRouteGuard from "@/components/ArabicRouteGuard";
import euEspaLogo from "@/assets/eu-espa-logo.jpg";
import greece20BannerEl from "@/assets/greece20-floating-white-el.png.asset.json";
import greece20BannerEn from "@/assets/greece20-floating-white-en.png.asset.json";
import { useLanguage } from "@/contexts/LanguageContext";
import Index from "./pages/Index";
import Services from "./pages/Services";

// Services pages
import ChronicConditionManagement from "./pages/services/ChronicConditionManagement";
import Telemedicine from "./pages/services/Telemedicine";
import DigitalTherapeutics from "./pages/services/DigitalTherapeutics";
import MarineTelemedicine from "./pages/services/MarineTelemedicine";
import Consulting from "./pages/services/Consulting";
import ActiveAgingSOS from "./pages/services/ActiveAgingSOS";
import MentalHealth from "./pages/services/MentalHealth";

// About pages
import VidavoEU from "./pages/about/VidavoEU";
import VidavoIndia from "./pages/about/VidavoIndia";
import VidavoMiddleEast from "./pages/about/VidavoMiddleEast";
import Vidavo from "./pages/about/Vidavo";
import WhyUs from "./pages/about/WhyUs";
import InvestorRelations from "./pages/about/InvestorRelations";
import Careers from "./pages/about/Careers";
import Vida24HomeCare from "./pages/about/Vida24HomeCare";

// R&D pages
import ResearchOnDemand from "./pages/rd/ResearchOnDemand";
import EUNationalProjects from "./pages/rd/EUNationalProjects";
import DigitalTransformation from "./pages/rd/DigitalTransformation";
import BuildingInnovation from "./pages/rd/BuildingInnovation";

// Legal pages
import PersonalData from "./pages/PersonalData";
const queryClient = new QueryClient();
const AppContent = () => {
  useGoogleAnalytics();
  const location = useLocation();
  const { language } = useLanguage();
  const isHiddenEspaPage = location.pathname === '/about/vidavo-eu-belgium' || location.pathname === '/about/vidavo-asia' || location.pathname === '/about/vida24-home-care';
  const isGreek = language === 'el';
  const greece20Img = isGreek ? greece20BannerEl.url : greece20BannerEn.url;
  const greece20Alt = isGreek
    ? "Ελλάδα 2.0 – Εθνικό Σχέδιο Ανάκαμψης και Ανθεκτικότητας. Με τη χρηματοδότηση της Ευρωπαϊκής Ένωσης."
    : "Greece 2.0 – National Recovery and Resilience Plan. Funded by the European Union – NextGenerationEU.";
  
  return <>
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<Index />} />
        <Route path="/services" element={<Services />} />
        
        {/* Services routes */}
        <Route path="/services/chronic-condition-management" element={<ChronicConditionManagement />} />
        <Route path="/services/telemedicine" element={<Telemedicine />} />
        <Route path="/services/digital-therapeutics" element={<DigitalTherapeutics />} />
        <Route path="/services/marine-telemedicine" element={<MarineTelemedicine />} />
        <Route path="/services/consulting" element={<Consulting />} />
        <Route path="/services/active-aging-sos" element={<ActiveAgingSOS />} />
        <Route path="/services/mental-health" element={<MentalHealth />} />
        
        {/* About routes */}
        <Route path="/about/vidavo-eu-belgium" element={<VidavoEU />} />
        <Route path="/about/vidavo-asia" element={<VidavoIndia />} />
        <Route path="/about/vidavo-india" element={<Navigate to="/about/vidavo-asia" replace />} />
        <Route path="/about/vidavo-middle-east" element={<VidavoMiddleEast />} />
        <Route path="/about/vidavo-eu" element={<Navigate to="/about/vidavo-eu-belgium" replace />} />
        <Route path="/growth" element={<Navigate to="/rd/building-innovation" replace />} />
        <Route path="/about/vidavo" element={<Vidavo />} />
        <Route path="/about/why-us" element={<WhyUs />} />
        <Route path="/about/investor-relations" element={<InvestorRelations />} />
        <Route path="/about/careers" element={<Careers />} />
        <Route path="/about/vida24-home-care" element={<Vida24HomeCare />} />
        
        {/* R&D routes */}
        <Route path="/rd/research-on-demand" element={<ResearchOnDemand />} />
        <Route path="/rd/eu-national-projects" element={<EUNationalProjects />} />
        <Route path="/rd/building-innovation" element={<BuildingInnovation />} />
        <Route path="/rd/digital-transformation" element={<DigitalTransformation />} />
        
        {/* Legal routes */}
        <Route path="/personal-data" element={<PersonalData />} />
        
        {/* Catch-all route - redirect any unmatched URLs to home */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      {/* Funding stickers - Global (Greece 2.0 / NextGenerationEU + ESPA) */}
      <div className="fixed bottom-24 right-8 z-50 flex flex-col items-end gap-3">
        <Link
          to="/rd/digital-transformation"
          aria-label="Learn more about our Digital Transformation project"
          className="block w-64 md:w-80 rounded-md shadow-lg overflow-hidden transition-transform hover:scale-105 hover:shadow-2xl"
        >
          <img
            src={greece20Img}
            alt={greece20Alt}
            className="w-full h-auto object-contain"
          />
        </Link>
        {!isHiddenEspaPage && (
          <Link
            to="/rd/eu-national-projects?open=IM"
            aria-label="Learn more about the EU ESPA Program - Central Macedonia 2021-2027"
            className={`block w-64 md:w-80 rounded-md shadow-lg overflow-hidden transition-transform hover:scale-105 hover:shadow-2xl ${isGreek ? 'py-2 md:py-[10px]' : 'py-[9px] md:py-[11.5px]'}`}
          >
            <img
              src={euEspaLogo}
              alt="EU ESPA Program - Central Macedonia 2021-2027"
              className="w-full h-auto object-contain"
            />
          </Link>
        )}
      </div>
    </>;
};
const App = () => <QueryClientProvider client={queryClient}>
    <LanguageProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <CookieConsent>
          <BrowserRouter>
            <ArabicRouteGuard />
            <AppContent />
          </BrowserRouter>
        </CookieConsent>
      </TooltipProvider>
    </LanguageProvider>
  </QueryClientProvider>;
export default App;